import axios, { AxiosResponse } from "axios"
import { checkResponseStatus } from "@api/Utils"
import { EntityListResponseCSCacheMiraklProduct, EntityListResponseCSProductMapItem, EntityListResponseFarfetchProduct, EntityListResponseMiintoPickupPoint, EntityListResponsePackageType, EntityListResponseShop, EntityListResponseZalandoProduct } from 'cs.node.utils/csapi/provisioning'
import { BindingPointType } from "@pages/ShopsWarehousesBindings/ShopsWarehousesBindings"
import { UpdateWhsAndSyncResponse } from "../../../src/interfaces/Shops"
import { CloudStoreShopIdEnum } from "cs.node.utils/ShopUtils"
import { CurrentFilters } from "@utils/AdvancedFiltersUtils"

export type SubmitChannelShopResponse = {
    name: string,
    marketplace: string,
    success: boolean,
    error?: unknown
}

export const getShops = async ():Promise<EntityListResponseShop> =>{
    try {
        const response:AxiosResponse<EntityListResponseShop,any> = await axios.get('api/shops')
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getShop = async(csShopId: CloudStoreShopIdEnum, shopId: string):Promise<unknown> =>{
    try {
        const response:AxiosResponse<unknown,any> = await axios.get(`api/shops/${csShopId}/${shopId}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getChannelShops = async(csShopId: CloudStoreShopIdEnum):Promise<unknown[]> =>{
    try {
        const response:AxiosResponse<unknown[],any> = await axios.get(`api/shops/channel/${csShopId}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getShopPackageTypes = async(id: string):Promise<EntityListResponsePackageType> =>{
    try {
        const response:AxiosResponse<EntityListResponsePackageType,any> = await axios.get(`api/shops/${id}/package-types`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getMiintoPickupPoints = async(shopId: string, locationId: string) =>{
    try {
        const response:AxiosResponse<EntityListResponseMiintoPickupPoint,any> = await axios.get(`api/shops/miinto/${shopId}/pickup-points/${encodeURIComponent(locationId)}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}



export const setShopEnabled = async(shopId: string, enabled: boolean) =>{
    try {
        const response = await axios.patch(`api/shops/${shopId}/enabled`, {enabled})
        return response.data
    } catch(error:any) {
        throw error
    }
}

export const createShop = async(shop: unknown)=>{
    try {
        const response = await axios.post(`api/shops/create`, {shop})
        return response.data
    } catch(error: unknown) {
        throw error
    }
}

export const updateShop = async(shop: unknown)=>{
    try {
        const response = await axios.put(`api/shops/update`, {shop})
        return response.data
    } catch(error: unknown) {
        throw error
    }
}

export const submitChannelShops = async(cs_shop_id: CloudStoreShopIdEnum, shops: unknown[])=>{
    try {
        const response = await axios.post(`api/shops/channel`, {cs_shop_id, shops})
        return response.data
    } catch(error: unknown) {
        throw error
    }
}

export const updateWhsAndSync = async(bindingPoints: BindingPointType[], sync: boolean) =>{
    try {
        const response:UpdateWhsAndSyncResponse = await axios.post(`api/shops/update-whs-and-sync`, {bindingPoints, sync})
        return response.data
    } catch(error: unknown) {
        throw error
    }
}

export const renewShopToken = async(shopId: string, csShopId: string, tokenId: string)=>{
    try {
        const response = await axios.post(`api/shops/${shopId}/renew-token/`, {tokenId, csShopId})
        return response.data
    } catch(error: unknown) {
        throw error
    }
}

export const getShopItemAvailableQuantity = async(shopId: string, sku: string)=>{
    try {
        const response:AxiosResponse<{quantity: number},any> = await axios.get(`api/shops/${shopId}/sku-available-quantity/${encodeURIComponent(sku)}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getFarfetchProducts = async (filters:CurrentFilters, pageIndex?: number, pageSize?: number, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListResponseFarfetchProduct> =>{
    const shopId = filters.find(filter=>filter.id === "shop")?.value
    const sku = filters.find(filter=>filter.id === "sku" )?.value as string | undefined
    const barcode = filters.find(filter=>filter.id === "barcode" )?.value as string | undefined
    const storeBarcode = filters.find(filter=>filter.id === "storeBarcode" )?.value as string | undefined

    let sort: string | undefined
    if(sortObject && sortObject[0] === 0 && !!sortObject[1]) {
        sort = `itemId[${sortObject[1]}]`
    }

    const params = {
        pageIndex,
        pageSize,
        sort,
        sku,
        barcode,
        storeBarcode
    }
    try {
        const response:AxiosResponse<EntityListResponseFarfetchProduct,any> = await axios.get(`api/shops/farfetch/${shopId}/products`, {
            params,
            signal: controller?.signal
        })

        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getMiintoProducts = async (filters:CurrentFilters, pageIndex?: number, pageSize?: number, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListResponseCSProductMapItem> =>{
    const shopId = filters.find(filter=>filter.id === "shop")?.value
    const miinto_id = filters.find(filter=>filter.id === "miinto_id" )?.value as string | undefined
    const miinto_item_id = filters.find(filter=>filter.id === "miinto_item_id" )?.value as string | undefined

    let sort: string | undefined
    if(sortObject && sortObject[0] === 0 && !!sortObject[1]) {
        sort = `miinto_id[${sortObject[1]}]`
    }

    const params = {
        pageIndex,
        pageSize,
        sort,
        miinto_id,
        miinto_item_id,
    }
    try {
        const response:AxiosResponse<EntityListResponseCSProductMapItem,any> = await axios.get(`api/shops/miinto/${shopId}/products`, {
            params,
            signal: controller?.signal
        })

        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getDecathlonProducts = async (filters:CurrentFilters, pageIndex?: number, pageSize?: number, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListResponseCSCacheMiraklProduct> =>{
    const shopId = filters.find(filter=>filter.id === "shop")?.value
    const offer_sku = filters.find(filter=>filter.id === "offer_sku" )?.value as string | undefined
    const product_sku = filters.find(filter=>filter.id === "product_sku" )?.value as string | undefined
    const offer_id = filters.find(filter=>filter.id === "offer_id" )?.value as string | undefined

    let sort: string | undefined
    if(sortObject && sortObject[0] === 0 && !!sortObject[1]) {
        sort = `offer_id[${sortObject[1]}]`
    }

    const params = {
        pageIndex,
        pageSize,
        sort,
        offer_sku,
        product_sku,
        offer_id
    }
    try {
        const response:AxiosResponse<EntityListResponseCSCacheMiraklProduct,any> = await axios.get(`api/shops/decathlon/${shopId}/products`, {
            params,
            signal: controller?.signal
        })

        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getPersonalShopProducts = async (filters:CurrentFilters, pageIndex?: number, pageSize?: number, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListResponseCSCacheMiraklProduct> =>{
    const shopId = filters.find(filter=>filter.id === "shop")?.value
    const sku = filters.find(filter=>filter.id === "sku" )?.value as string | undefined
    const shop_item_id = filters.find(filter=>filter.id === "shop_item_id" )?.value as string | undefined

    let sort: string | undefined
    if(sortObject && sortObject[0] === 0 && !!sortObject[1]) {
        sort = `sku[${sortObject[1]}]`
    }

    const params = {
        pageIndex,
        pageSize,
        sort,
        sku,
        shop_item_id
    }
    try {
        const response:AxiosResponse<EntityListResponseCSCacheMiraklProduct,any> = await axios.get(`api/shops/personalshop/${shopId}/products`, {
            params,
            signal: controller?.signal
        })

        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getZalandoProducts = async (filters:CurrentFilters, pageIndex?: number, pageSize?: number, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListResponseZalandoProduct> =>{
    const shopId = filters.find(filter=>filter.id === "shop")?.value
    const ean = filters.find(filter=>filter.id === "ean" )?.value as string | undefined
    const sku = filters.find(filter=>filter.id === "sku" )?.value as string | undefined
    const simple_id = filters.find(filter=>filter.id === "simple_id" )?.value as string | undefined

    let sort: string | undefined
    if(sortObject && sortObject[0] === 0 && !!sortObject[1]) {
        sort = `ean[${sortObject[1]}]`
    }

    const params = {
        pageIndex,
        pageSize,
        sort,
        ean,
        sku,
        simple_id
    }
    try {
        const response:AxiosResponse<EntityListResponseZalandoProduct,any> = await axios.get(`api/shops/zalando/${shopId}/products`, {
            params,
            signal: controller?.signal
        })

        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const singleShopRecalculateItemQuantities = async(shopId: string)=>{
    try {
        const response:AxiosResponse<{status: "ok" | "ko"},any> = await axios.post(`api/shops/${shopId}/recalculate-item-quantities`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const bulkShopsRecalculateItemQuantities = async()=>{
    try {
        const response:AxiosResponse<{status: "ok" | "ko", results: PromiseSettledResult<any>[]},any> = await axios.post(`api/shops/bulk-recalculate-item-quantities`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}